/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2019-08-29 12:30:39
 *
 *******************************************************/
// @flow
import _ from 'lodash';
import { capitalizeFirstLetter } from './index';
import { stringifyRGBASpecialFormat } from 'src/helpers/color-helper';
import { FILTER_SETTINGS_SORTING_TYPES } from 'src/constants/filter-settings';
/**
 * Build initial data for filter settings
 *
 * @param  {Object} filterSettings filter settings from API
 *
 * @return {Object} initialValue
 */
export const buildInitialFilterSettingsValues = (filterSettings: Object, i18n: Object) => {
  const customSortingListString = _.get(filterSettings, 'general.customSortingList', '');
  let customSortingList = [];

  const formattedSortingTypes = FILTER_SETTINGS_SORTING_TYPES.map(function (type) {
    return {
      ...type,
      label: i18n.translate(type.label)
    };
  });

  if (!_.isEmpty(customSortingListString) && _.isString(customSortingListString)) {
    const sortingListArray = _.filter(customSortingListString.split('|'), function (item) {
      return !_.isEmpty(item);
    });
    customSortingList = sortingListArray.map(
      item =>
        _.find(formattedSortingTypes, ({ value }) => value === item) || { value: item, label: item }
    );
  }

  const productAndVariantAvailable = _.get(
    filterSettings,
    'general.productAndVariantAvailable',
    false
  );
  const availableAfterFiltering = _.get(filterSettings, 'general.availableAfterFiltering', false);

  const outOfStockDisplayOption = availableAfterFiltering
    ? 'hideAfterFiltering'
    : productAndVariantAvailable
    ? 'hide'
    : 'show';

  const showVariantImageBasedOnSelectedFilter = _.get(
    filterSettings,
    'general.showVariantImageBasedOnSelectedFilter',
    ''
  );

  const showVariantImageBasedOn = showVariantImageBasedOnSelectedFilter.replace('pf_opt_', '');

  // canonical URL, opposite of addCollectionToProductUrl
  const useCanonicalUrl = !_.get(filterSettings, 'general.addCollectionToProductUrl', false);

  // Shorten URL
  const urlScheme = _.get(filterSettings, 'general.urlScheme', 1);
  const isShortenUrlParam = _.get(filterSettings, 'general.isShortenUrlParam', false);
  const shortenURL = urlScheme === 2 && isShortenUrlParam;

  return {
    displaySetting: {
      showFilterOptionCount: _.get(filterSettings, 'general.showFilterOptionCount', false),
      showRefineBy: _.get(filterSettings, 'general.showRefineBy', false),
      showOutOfStockOption: _.get(filterSettings, 'general.showOutOfStockOption', false),
      showSingleOption: _.get(filterSettings, 'general.showSingleOption', false),
      keepToggleState: _.get(filterSettings, 'general.keepToggleState', false),
      changeMobileButtonLabel: _.get(filterSettings, 'general.changeMobileButtonLabel', false),
      sortingAvailableFirst: _.get(filterSettings, 'general.sortingAvailableFirst', false),
      outOfStockDisplayOption,
      showLoading: _.get(filterSettings, 'general.showLoading', false),
      activeScrollToTop: _.get(filterSettings, 'general.activeScrollToTop', false),
      paginationType: _.get(filterSettings, 'general.paginationType', 'default'),
      customSortingList,
      useCanonicalUrl,
      showVariantImageBasedOn,
      shortenURL
    },
    style: {
      // Filter title
      filterTitleFontFamily: _.get(filterSettings, 'style.filterTitleFontFamily', ''),
      filterTitleFontSize: _.get(filterSettings, 'style.filterTitleFontSize', ''),
      filterTitleFontTransform: _.get(filterSettings, 'style.filterTitleFontTransform', ''),
      filterTitleFontWeight: `${_.get(filterSettings, 'style.filterTitleFontWeight', '')}`,
      filterTitleTextColor: _.get(filterSettings, 'style.filterTitleTextColor', ''),

      // Filter option
      filterOptionFontFamily: _.get(filterSettings, 'style.filterOptionFontFamily', ''),
      filterOptionFontSize: _.get(filterSettings, 'style.filterOptionFontSize', ''),
      filterOptionTextColor: _.get(filterSettings, 'style.filterOptionTextColor', ''),

      // Mobile button
      filterMobileButtonBackgroundColor: _.get(
        filterSettings,
        'style.filterMobileButtonBackgroundColor',
        ''
      ),
      filterMobileButtonFontFamily: _.get(filterSettings, 'style.filterMobileButtonFontFamily', ''),
      filterMobileButtonFontSize: _.get(filterSettings, 'style.filterMobileButtonFontSize', ''),
      filterMobileButtonFontTransform: _.get(
        filterSettings,
        'style.filterMobileButtonFontTransform',
        ''
      ),
      filterMobileButtonFontWeight: `${_.get(
        filterSettings,
        'style.filterMobileButtonFontWeight',
        ''
      )}`,
      filterMobileButtonTextColor: _.get(filterSettings, 'style.filterMobileButtonTextColor', '')
    }
  };
};

/**
 * Build data for saving filter settings
 *
 * @param  {Object} data filter settings data
 * @param  {Object} filterSettings filter settings from API
 *
 * @return {Object} value value for sending API
 */
export const buildFilterSettingsValues = (data: Object, filterSettings: Object) => {
  const {
    displaySetting: {
      customSortingList,
      outOfStockDisplayOption,
      useCanonicalUrl,
      showVariantImageBasedOn,
      shortenURL
    },
    style: {
      filterTitleTextColor: titleTextColor,
      filterTitleFontSize: titleFontSize,
      filterTitleFontWeight: titleFontWeight,
      filterOptionTextColor: optionTextColor,
      filterOptionFontSize: optionFontSize,
      filterMobileButtonTextColor: mobileTextColor,
      filterMobileButtonBackgroundColor: mobileBackgroundColor,
      filterMobileButtonFontSize: mobileButtonFontSize,
      filterMobileButtonFontWeight: mobileButtonFontWeight
    }
  } = data;
  let customSortingListString = '';
  if (!_.isEmpty(customSortingList)) {
    customSortingList.map((item, index) => {
      customSortingListString += item.value;
      if (index < customSortingList.length - 1) {
        customSortingListString += '|';
      }
      return '';
    });
  }

  let productAndVariantAvailable, availableAfterFiltering;

  switch (outOfStockDisplayOption) {
    case 'show': {
      productAndVariantAvailable = false;
      availableAfterFiltering = false;
      break;
    }
    case 'hide': {
      productAndVariantAvailable = true;
      availableAfterFiltering = false;
      break;
    }
    default: {
      // hideAfterFiltering
      productAndVariantAvailable = false;
      availableAfterFiltering = true;
    }
  }

  // Convert color from hsv to rgba string
  const filterTitleTextColor = convertColorToSpecialFormat(titleTextColor);
  const filterOptionTextColor = convertColorToSpecialFormat(optionTextColor);
  const filterMobileButtonTextColor = convertColorToSpecialFormat(mobileTextColor);
  const filterMobileButtonBackgroundColor = convertColorToSpecialFormat(mobileBackgroundColor);

  // Convert to number
  const filterTitleFontSize = titleFontSize ? parseInt(titleFontSize) : '';
  const filterTitleFontWeight = titleFontWeight ? parseInt(titleFontWeight) : '';
  const filterOptionFontSize = optionFontSize ? parseInt(optionFontSize) : '';
  const filterMobileButtonFontSize = mobileButtonFontSize ? parseInt(mobileButtonFontSize) : '';
  const filterMobileButtonFontWeight = mobileButtonFontWeight
    ? parseInt(mobileButtonFontWeight)
    : '';

  // Shorten URL
  let urlScheme = _.get(filterSettings, 'general.urlScheme', 1);
  let isShortenUrlParam = _.get(filterSettings, 'general.isShortenUrlParam', false);
  if (shortenURL) {
    urlScheme = 2;
    isShortenUrlParam = true;
  } else {
    isShortenUrlParam = urlScheme !== 2 ? isShortenUrlParam : false;
    urlScheme = urlScheme === 2 ? 1 : urlScheme;
  }

  const { general, style } = filterSettings;
  return {
    ...filterSettings,
    general: {
      ...general,
      ...data.displaySetting,
      productAndVariantAvailable,
      availableAfterFiltering,
      addCollectionToProductUrl: !useCanonicalUrl,
      useCanonicalUrl: undefined,
      outOfStockDisplayOption: undefined,
      customSortingList: customSortingListString,
      showVariantImageBasedOnSelectedFilter: _.isEmpty(showVariantImageBasedOn)
        ? ''
        : `pf_opt_${showVariantImageBasedOn}`,
      // Shorten URL,
      urlScheme,
      isShortenUrlParam,
      shortenURL: undefined,

      //fix issue redundant data, will remove after
      filterTitleFontFamily: undefined,
      filterTitleFontSize: undefined,
      filterTitleFontTransform: undefined,
      filterTitleFontWeight: undefined,
      filterTitleTextColor: undefined,

      // Filter option
      filterOptionFontFamily: undefined,
      filterOptionFontSize: undefined,
      filterOptionTextColor: undefined,

      // Mobile button
      filterMobileButtonBackgroundColor: undefined,
      filterMobileButtonFontFamily: undefined,
      filterMobileButtonFontSize: undefined,
      filterMobileButtonFontTransform: undefined,
      filterMobileButtonFontWeight: undefined,
      filterMobileButtonTextColor: undefined
    },
    style: {
      ...style,
      ...data.style,
      filterTitleTextColor,
      filterTitleFontSize,
      filterTitleFontWeight,
      filterOptionTextColor,
      filterOptionFontSize,
      filterMobileButtonTextColor,
      filterMobileButtonBackgroundColor,
      filterMobileButtonFontSize,
      filterMobileButtonFontWeight,
      //fix issue redundant data, will remove after
      showFilterOptionCount: undefined,
      showRefineBy: undefined,
      showOutOfStockOption: undefined,
      showSingleOption: undefined,
      keepToggleState: undefined,
      changeMobileButtonLabel: undefined,
      productAndVariantAvailable: undefined,
      availableAfterFiltering: undefined,
      sortingAvailableFirst: undefined,
      showLoading: undefined,
      activeScrollToTop: undefined,
      paginationType: undefined,
      customSortingList: undefined
    }
  };
};

/**
 * Convert HSV color to RGBA string under format rbga(r||g||b||a)
 * @param  {object} hsvColor
 * @return {string}          rgba string color
 */
export const convertColorToSpecialFormat = (color: Object) => {
  // Return original color if it is not hsv
  if (!_.has(color, 'rgb')) {
    return color;
  }

  return stringifyRGBASpecialFormat(color.rgb);
};

/**
 * Build list of options for showing variant image by from optionList
 * @param  {Array} optionList list of option from store
 * @return {Array}   list list of option for select
 */

export const buildShowVariantImageBasedOnOption = (
  optionList: Array<string>,
  locale: string,
  i18n: Object
) => {
  const result = _.map(optionList, item => ({
    value: item.name,
    label: capitalizeFirstLetter(item.originalName, locale)
  }));
  result.unshift({
    value: '',
    label: i18n.translate(
      'AdminHTML.FilterSettings.Display.productListTab_FieldShowVariantImageBasedOn_DefaultOption'
    )
  });
  return result;
};
